/**
 * 支付组件 dropin：根据渠道选择 shared 支付编排并负责预加载渠道 SDK。
 */
import { PublishPaymentMethodItem } from '@web-publish-id/payment-shared/apis/types/payment'
import type { PaymentComponent } from './payment'
import {
  PaymentComponentCreateDropinOptions,
  PaymentComponentDropinAllinpayRenderOptions,
  PaymentComponentDropinHandlePayload,
  PaymentComponentDropinRenderOptions,
} from '../types'
import { createWalletDropin } from '@web-publish-id/payment-shared/payments/walletDropin'
import { createAdyenDropIn } from '@web-publish-id/payment-shared/payments/adyenDropIn'
import { createAllinpayDropIn } from '@web-publish-id/payment-shared/payments/allinpayDropIn'
import { createAlipayDropIn } from '@web-publish-id/payment-shared/payments/alipayDropIn'
import { createWechatPayDropIn } from '@web-publish-id/payment-shared/payments/wechatPayDropIn'
import { createSandboxDropIn } from '@web-publish-id/payment-shared/payments/sandboxDropIn'
import type { PaymentComponentCore } from '../core'
import { PAYMENT_COM_SDK_VERSION } from '../const/sdk'
import { createPayermaxDropin } from '@web-publish-id/payment-shared/payments/payermaxDropin'
import { createChecusDropin } from '@web-publish-id/payment-shared/payments/checusDropin'
import { PaymentDependencyManager } from '@web-publish-id/payment-shared/utils/dependency'

/**
 * 支付组件dropin
 */
export class PaymentComponentDropin {
  core: PaymentComponentCore
  paymentInstance: PaymentComponent
  methodConfig: PublishPaymentMethodItem
  options: PaymentComponentCreateDropinOptions

  // 依赖管理器
  dependencyManager: PaymentDependencyManager

  // 支付单id
  paymentId: string
  private wechatPayDropin?: ReturnType<typeof createWechatPayDropIn>

  constructor(
    paymentInstance: PaymentComponent,
    methodConfig: PublishPaymentMethodItem,
    options: PaymentComponentCreateDropinOptions,
  ) {
    this.paymentInstance = paymentInstance
    this.methodConfig = methodConfig
    this.options = options
    this.core = paymentInstance.core
    this.dependencyManager = new PaymentDependencyManager()

    // 加载依赖
    this.dependencyManager.loadDependency().catch((error) => {
      console.error('Failed to preload payment SDKs', error)
    })
  }

  /**
   * 渲染支付组件
   * @param containerId 容器id
   * @returns 微信扫码渠道返回 destroy 句柄，离开支付区域时调用以停止查单。
   */
  async render(containerId: string, options: PaymentComponentDropinRenderOptions = {}) {
    switch (this.methodConfig.payment_channel_type) {
      case 1:
        return this.renderAdyen(containerId)

      case 4:
        return this.renderPayermax(containerId)

      case 10:
        return this.renderChecus(containerId)

      case 5:
        return this.renderWallet(containerId)

      case 7:
        return this.renderAllinpay(containerId, options.allinpay)

      case 12:
        return this.renderAlipay(containerId)

      case 13:
        return this.renderWechatPay(containerId)

      case 11:
        return this.renderSandbox(containerId)
      default:
        throw new Error('Unsupported payment channel type')
    }
  }

  /**
   * 渲染Adyen支付组件
   * @param containerId 容器id
   * @returns
   */
  renderAdyen(containerId: string) {
    const { adyenConfig, configuration } = this.core
    if (!adyenConfig) {
      throw new Error('Adyen config is not set')
    }

    const { language } = configuration
    const { client_key } = adyenConfig

    const { setupFrom } = createAdyenDropIn({
      clientKey: client_key,
      locale: language!,
      orderInfo: this.paymentInstance.orderInfo,
      sdkVersion: PAYMENT_COM_SDK_VERSION,
      getPaymentId: () => this.paymentId,
      ...this.generateHandles(),
    })

    return setupFrom(containerId, this.methodConfig)
  }

  /**
   * 渲染钱包支付组件
   * @param containerId 容器id
   * @returns
   */
  renderWallet(containerId: string) {
    const { client_id, language } = this.core.configuration
    const { account_token } = this.paymentInstance.config

    const walletDropin = createWalletDropin({
      token: account_token!,
      clientId: client_id,
      language: language!,
      sdkVersion: PAYMENT_COM_SDK_VERSION,
      orderInfo: this.paymentInstance.orderInfo,
      ...this.generateHandles(),
    })

    return walletDropin.setup(containerId, this.methodConfig)
  }

  /**
   * 渲染通联支付组件
   * @param containerId 容器id
   * @returns
   */
  renderAllinpay(containerId: string, options?: PaymentComponentDropinAllinpayRenderOptions) {
    const { client_id, language } = this.core.configuration

    const allinpayDropin = createAllinpayDropIn({
      clientId: client_id,
      language,
      orderInfo: this.paymentInstance.orderInfo,
      sdkVersion: PAYMENT_COM_SDK_VERSION,
      ...this.generateHandles(),
    })

    // 只显示二维码
    if (options?.onlyQRCode) {
      return allinpayDropin.setupOnlyQRCode(containerId, this.methodConfig)
    }

    return allinpayDropin.setup(containerId, this.methodConfig)
  }

  /**
   * 渲染支付宝支付组件
   * @param containerId 容器id
   * @returns
   */
  renderAlipay(containerId: string) {
    const { client_id, language } = this.core.configuration

    const alipayDropin = createAlipayDropIn({
      clientId: client_id,
      language,
      orderInfo: this.paymentInstance.orderInfo,
      sdkVersion: PAYMENT_COM_SDK_VERSION,
      ...this.generateHandles(),
    })

    return alipayDropin.setup(containerId, this.methodConfig)
  }

  /** 渲染独立微信扫码支付组件，返回用于停止查单并卸载组件的句柄。 */
  renderWechatPay(containerId: string) {
    const { client_id, language } = this.core.configuration
    this.wechatPayDropin?.destroy()
    this.wechatPayDropin = createWechatPayDropIn({
      clientId: client_id,
      language,
      orderInfo: this.paymentInstance.orderInfo,
      sdkVersion: PAYMENT_COM_SDK_VERSION,
      ...this.generateHandles(),
    })
    return this.wechatPayDropin.setup(containerId, this.methodConfig)
  }

  /**
   * 渲染Payermax支付组件
   * @param containerId 容器id
   * @returns
   */
  renderPayermax(containerId: string) {
    const { language } = this.core.configuration

    const payermaxDropin = createPayermaxDropin({
      locale: language!,
      orderInfo: this.paymentInstance.orderInfo,
      paymentMethod: this.methodConfig,
      callbackUrl: this.options.callback_url,
      sdkVersion: PAYMENT_COM_SDK_VERSION,
      isSandbox: import.meta.env.MODE !== 'production',
      ...this.generateHandles(),
    })

    return payermaxDropin.setupFrom(containerId)
  }

  /**
   * 渲染Checus支付组件(机械复刻 renderPayermax,仅 dropin 工厂改为 createChecusDropin)
   * @param containerId 容器id
   * @returns
   */
  renderChecus(containerId: string) {
    const { language } = this.core.configuration

    const checusDropin = createChecusDropin({
      locale: language!,
      orderInfo: this.paymentInstance.orderInfo,
      paymentMethod: this.methodConfig,
      callbackUrl: this.options.callback_url,
      sdkVersion: PAYMENT_COM_SDK_VERSION,
      isSandbox: import.meta.env.MODE !== 'production',
      ...this.generateHandles(),
    })

    return checusDropin.setupFrom(containerId)
  }

  renderSandbox(containerId: string) {
    const { client_id } = this.core.configuration

    const sandboxDropin = createSandboxDropIn({
      clientId: client_id,
      orderInfo: this.paymentInstance.orderInfo,
      sdkVersion: PAYMENT_COM_SDK_VERSION,
      ...this.generateHandles(),
    })

    return sandboxDropin.setup(containerId, this.methodConfig)
  }

  /*
   * 生成支付组件dropin handles
   * @returns
   */
  private generateHandles() {
    const payload: PaymentComponentDropinHandlePayload = {
      orderInfo: this.paymentInstance.orderInfo,
      paymentMethod: this.methodConfig,
    }
    return {
      onSubmit: () => this.options.onSubmit?.(payload),
      onError: (error: any) => {
        this.options.onError?.(payload, error)
      },
      onCreateOrder: (result: any) => {
        this.paymentId = result.payment_id
        this.options.onCreateOrder?.(payload)
      },
      onCompleted: () => this.options.onCompleted?.(payload),
      onFailed: (result: any) => {
        payload.failed = result
        this.options.onFailed?.(payload)
      },
      onLoading: (loading: boolean) => {
        this.options.onLoading?.(loading)
      },
    }
  }
}
